import React from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, Inbox, ShieldCheck, Building2, FolderKanban,
  GanttChartSquare, Bot, FolderOpen, ScrollText, Zap, ChevronDown,
  Radar, Brain, Workflow, Network, Boxes, Palette,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/brief", label: "Executive Brief", icon: Radar },
  { to: "/orchestration", label: "Orchestration", icon: Workflow },
  { to: "/directives", label: "Directives", icon: Inbox },
  { to: "/approvals", label: "Approvals", icon: ShieldCheck, badgeKey: "approvals" },
  { to: "/owner-mind", label: "Owner Mind", icon: Brain },
  { to: "/org", label: "Company Org", icon: Network },
  { to: "/agent-os", label: "Agent OS", icon: Boxes },
  { to: "/design-packets", label: "Design Packets", icon: Palette },
  { to: "/clients", label: "Clients", icon: Building2 },
  { to: "/jobs", label: "Jobs", icon: FolderKanban },
  { to: "/schedule", label: "Schedule / CPM", icon: GanttChartSquare },
  { to: "/agents", label: "Agents", icon: Bot },
  { to: "/files", label: "Knowledge / Files", icon: FolderOpen },
  { to: "/audit", label: "Audit Trail", icon: ScrollText },
];

export default function Sidebar() {
  const { companyId, setCompanyId, companies, activeCompany, companyParam } = useApp();

  const { data: approvals = [] } = useQuery({
    queryKey: ["approvals", companyId, "pending"],
    queryFn: async () => (await api.get("/approvals", { params: { ...companyParam, status: "pending" } })).data,
    refetchInterval: 20000,
  });

  return (
    <aside className="w-64 shrink-0 bg-ink text-white flex flex-col h-screen sticky top-0" data-testid="sidebar">
      {/* Brand */}
      <div className="px-5 py-5 border-b border-white/10">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-brand flex items-center justify-center">
            <Zap className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-extrabold text-[15px] tracking-tight">MERIDIAN</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Control Tower</div>
          </div>
        </div>
      </div>

      {/* Company switcher */}
      <div className="px-3 pt-4 pb-2">
        <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-white/40 px-2 mb-2">Workspace</div>
        <div className="space-y-0.5" data-testid="company-switcher">
          <CompanyItem active={companyId === "all"} onClick={() => setCompanyId("all")} color="#A1A1AA" name="All Companies" />
          {companies.map((c) => (
            <CompanyItem
              key={c.id}
              active={companyId === c.id}
              onClick={() => setCompanyId(c.id)}
              color={c.color}
              name={c.name}
              testId={`company-option-${c.id}`}
            />
          ))}
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 pt-3">
        <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-white/40 px-2 mb-2">Operations</div>
        <div className="space-y-0.5">
          {NAV.map((item) => {
            const badge = item.badgeKey === "approvals" ? approvals.length : 0;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2 text-sm font-medium transition-colors border-l-2 ${
                    isActive
                      ? "bg-white/10 border-brand text-white"
                      : "border-transparent text-white/60 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <item.icon className="w-4 h-4" strokeWidth={2} />
                <span className="flex-1">{item.label}</span>
                {badge > 0 && (
                  <span className="min-w-[20px] h-5 px-1 flex items-center justify-center bg-st-warning text-ink text-[11px] font-bold font-mono">
                    {badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>

      <div className="px-5 py-3 border-t border-white/10 text-[10px] text-white/40 font-mono">
        Evidence-backed autonomy · v1
      </div>
    </aside>
  );
}

function CompanyItem({ active, onClick, color, name, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full flex items-center gap-2.5 px-2 py-1.5 text-sm transition-colors ${
        active ? "bg-white/10 text-white" : "text-white/55 hover:text-white hover:bg-white/5"
      }`}
    >
      <span className="w-2.5 h-2.5 shrink-0" style={{ backgroundColor: color }} />
      <span className="truncate text-left flex-1">{name}</span>
    </button>
  );
}
